import cx from 'classnames';
import { ReactNode } from 'react';
import styles from '../sass/components/Tab.module.scss';

interface TabProps {
  children?: ReactNode;
  selected: boolean;
  className?: string;
  onSelect?: () => void;
  step?: boolean;
  tab?: boolean;
  disabled?: boolean;
  presentational?: boolean;
}

/**
 * One cell of a TabContainer strip. It is a container, never a control
 * (QA 2026-09-25 #29): the booking steps put their one link inside it, and
 * the service pickers use it as a heading band. It used to be a
 * `div role="menuitem" tabIndex=0` with no menu around it - an axe critical,
 * and a second tab stop in front of every step's own link. A step is an
 * `<li>` of the steps' `<ol>`; anything else stays a `<div>`. The Sass reset
 * already zeroes and unbullets lists, and every rule here is by class, so
 * nothing moves on screen.
 */
function Tab({
  children,
  selected = false,
  onSelect,
  className,
  step,
  tab,
  disabled = false,
  presentational,
}: TabProps) {
  const classes = cx(
    {
      [styles.selected]: selected,
      [styles.tab]: tab,
      [styles.step]: step,
      [styles.disabled]: disabled,
      [styles.presentational]: presentational,
    },
    className,
    'label',
  );
  if (step) {
    return <li className={classes}>{children}</li>;
  }
  return (
    // Only a pointer shortcut for callers that pass onSelect; the keyboard
    // route is the control the caller puts inside.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={onSelect && !disabled ? () => onSelect() : undefined}
      className={classes}
    >
      {children}
    </div>
  );
}

export default Tab;
